'use client';

import { qsDeserializeRaw, qsSerializeRaw } from '@websolutespa/bom-core';
import { useRouter } from 'next/compat/router';
import { NextRouter } from 'next/router';
import { useCallback } from 'react';

export type IParamsQs = Record<string, any>;

export type IUseSearchParamsQsResult = {
  params: IParamsQs,
  pushParams: (params: IParamsQs) => void
  pushParamsSilently: (params: IParamsQs) => void,
  replaceParams: (params: IParamsQs) => void
  replaceParamsSilently: (params: IParamsQs) => void,
};

function deserialize(encoded: string | null | undefined, key?: string): IParamsQs {
  let decoded = {};
  if (encoded) {
    decoded = qsDeserializeRaw(encoded);
  }
  if (key && decoded) {
    decoded = decoded[key as keyof typeof decoded];
  }
  return decoded || {};
}

function encode(params: IParamsQs, value: IParamsQs, key?: string): { search: string, params: IParamsQs } {
  if (key) {
    params = { ...params, [key]: value };
  } else if (typeof value === 'object') {
    params = { ...params, ...value };
  } else {
    params = { ...params };
  }
  const search = qsSerializeRaw(params);
  return { search, params };
}

function serialize(search: string, value: IParamsQs, key?: string): { search: string, params: IParamsQs } {
  const params = deserialize(search);
  const encoded = encode(params, value, key);
  return encoded;
}

function getComponents(url: string): { pathname: string, search: string } {
  const components = decodeURI(url).split('?');
  const pathname = components[0]!.split('#')[0]!;
  const search = components[1] || '';
  // console.log('getComponents', pathname, search);
  return { pathname, search };
}

export function getSearchParamsQs(url: string, key?: string): IParamsQs {
  const components = getComponents(url);
  const value = deserialize(components.search, key);
  return value;
}

export function updateSearchParamsQs(url: string, value: IParamsQs, key?: string): { pathname: string, query: string } {
  const components = getComponents(url);
  const { search, params } = serialize(components.search, value, key);
  return { pathname: components.pathname, query: search };
}

export function pushSearchParamsQs(router: NextRouter, value: IParamsQs, key?: string): void {
  const { pathname, query } = updateSearchParamsQs(router?.asPath, value, key);
  router.push({ pathname, query });
}

export function pushSearchParamsQsSilently(router: NextRouter, value: IParamsQs, key?: string): void {
  const { pathname, query } = updateSearchParamsQs(router?.asPath, value, key);
  router.push(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export function replaceSearchParamsQs(router: NextRouter, value: IParamsQs, key?: string): void {
  const { pathname, query } = updateSearchParamsQs(router?.asPath, value, key);
  router?.replace({ pathname, query });
}

export function replaceSearchParamsQsSilently(router: NextRouter, value: IParamsQs, key?: string): void {
  const { pathname, query } = updateSearchParamsQs(router?.asPath, value, key);
  router?.replace(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export function useSearchParamsQs(key?: string): IUseSearchParamsQsResult {
  const router = useRouter();
  const params = router ? getSearchParamsQs(router?.asPath, key) : {};

  const pushParams = useCallback((params: IParamsQs) => {
    if (router) {
      pushSearchParamsQs(router, params, key);
    }
  }, [key, router]);

  const pushParamsSilently = useCallback((params: IParamsQs) => {
    if (router) {
      pushSearchParamsQsSilently(router, params, key);
    }
  }, [key, router]);

  const replaceParams = useCallback((params: IParamsQs) => {
    if (router) {
      replaceSearchParamsQs(router, params, key);
    }
  }, [key, router]);

  const replaceParamsSilently = useCallback((params: IParamsQs) => {
    if (router) {
      replaceSearchParamsQsSilently(router, params, key);
    }
  }, [key, router]);

  return { params, pushParamsSilently, pushParams, replaceParamsSilently, replaceParams };
}
